import { useState, useCallback } from 'react';
import {
  Button, Typography, Tag, Space, Table, Descriptions, message,
  Modal, Flex, Input, InputNumber, Select, Divider,
} from 'antd';
import {
  PlusOutlined, FileTextOutlined, UnorderedListOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { ColumnDef } from '@shared/types';
import { DISPLAY_DATE_FORMAT } from '@shared/constants';
import { DataTable, DetailDrawer } from '../../components/shared';
import type { FetchParams } from '../../components/shared';
import {
  fetchGRNs, getGRN, createGRN,
  type GRNHeader, type GRNDetail,
  type PODetail,
} from '../../api/purchasing';
import { api } from '../../api/client';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// ── Inspection status config ──

const INSPECTION_CONFIG: Record<string, { label: string; color: string }> = {
  passed: { label: 'Passed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  partial: { label: 'Partial', color: 'warning' },
};

// ── Column Definitions ──

const columns: ColumnDef<GRNHeader>[] = [
  { key: 'grnCode', label: 'GRN #', dataType: 'string', width: 130 },
  {
    key: 'poHeader', label: 'PO #', dataType: 'string', width: 130,
    render: (v) => {
      const po = v as GRNHeader['poHeader'];
      return po?.poCode || '—';
    },
  },
  { key: 'deliveryNoteRef', label: 'Delivery Note', dataType: 'string', width: 130 },
  { key: 'supplierInvoice', label: 'Supplier Invoice', dataType: 'string', width: 130 },
  { key: 'lineCount', label: 'Lines', dataType: 'number', width: 80 },
  { key: 'receivedOn', label: 'Received On', dataType: 'date', width: 130 },
  { key: 'createdAt', label: 'Created', dataType: 'date', width: 130 },
];

// ── GRN Line Table Columns ──

const grnLineColumns = [
  { title: 'PO Line ID', dataIndex: 'poLineId', key: 'poLineId', width: 100 },
  { title: 'Delivered', dataIndex: 'deliveredQty', key: 'deliveredQty', width: 90, align: 'right' as const },
  { title: 'Accepted', dataIndex: 'acceptedQty', key: 'acceptedQty', width: 90, align: 'right' as const },
  { title: 'Rejected', dataIndex: 'rejectedQty', key: 'rejectedQty', width: 90, align: 'right' as const },
  {
    title: 'Inspection', dataIndex: 'inspectionStatus', key: 'inspectionStatus', width: 110,
    render: (v: string) => {
      const cfg = INSPECTION_CONFIG[v] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
  { title: 'Notes', dataIndex: 'inspectionNotes', key: 'inspectionNotes', width: 150 },
  { title: 'Reject Reason', dataIndex: 'rejectReason', key: 'rejectReason', width: 130 },
];

// ── Create GRN Modal ──

interface GrnLineInput {
  key: string;
  poLineId: number;
  itemDescription: string;
  qty: number;
  balanceQty: number;
  deliveredQty: number;
  acceptedQty: number;
  rejectedQty: number;
  inspectionStatus: string;
  inspectionNotes: string;
}

function CreateGRNModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [poId, setPoId] = useState<number | null>(null);
  const [poDetail, setPoDetail] = useState<PODetail | null>(null);
  const [poLoading, setPoLoading] = useState(false);
  const [lines, setLines] = useState<GrnLineInput[]>([]);
  const [deliveryNoteRef, setDeliveryNoteRef] = useState('');
  const [supplierInvoice, setSupplierInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPO = async (id: number) => {
    setPoLoading(true);
    try {
      const res = await api.get(`/purchasing/pos/${id}`);
      const po = res.data as PODetail;
      setPoDetail(po);
      // Pre-fill lines from PO lines with remaining balance
      setLines(
        po.lines
          .filter((l) => l.balanceQty > 0)
          .map((l) => ({
            key: String(l.id),
            poLineId: l.id,
            itemDescription: l.itemDescription,
            qty: l.qty,
            balanceQty: l.balanceQty,
            deliveredQty: l.balanceQty,
            acceptedQty: l.balanceQty,
            rejectedQty: 0,
            inspectionStatus: 'passed',
            inspectionNotes: '',
          })),
      );
    } catch {
      message.error('Failed to load PO');
    } finally {
      setPoLoading(false);
    }
  };

  const updateLine = (key: string, field: keyof GrnLineInput, value: unknown) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };

  const handleSave = async () => {
    if (!poId) {
      message.error('Select a PO');
      return;
    }
    if (lines.length === 0) {
      message.error('No lines to receive');
      return;
    }
    for (const line of lines) {
      if (line.deliveredQty <= 0) {
        message.error('Delivered qty must be > 0');
        return;
      }
      if (line.acceptedQty + line.rejectedQty > line.deliveredQty) {
        message.error('Accepted + rejected cannot exceed delivered qty');
        return;
      }
    }

    setSaving(true);
    try {
      await createGRN({
        poId,
        deliveryNoteRef: deliveryNoteRef || undefined,
        supplierInvoice: supplierInvoice || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({
          poLineId: l.poLineId,
          deliveredQty: l.deliveredQty,
          acceptedQty: l.acceptedQty,
          rejectedQty: l.rejectedQty,
          inspectionStatus: l.inspectionStatus as 'passed' | 'failed' | 'partial',
          inspectionNotes: l.inspectionNotes || undefined,
        })),
      });
      message.success('GRN created');
      handleClose();
      onCreated();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to create GRN';
      message.error(msg || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setPoId(null);
    setPoDetail(null);
    setLines([]);
    setDeliveryNoteRef('');
    setSupplierInvoice('');
    setNotes('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Create Goods Receipt Note"
      width={1000}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            disabled={lines.length === 0}
            icon={<InboxOutlined />}
          >
            Create GRN
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Flex gap={16} style={{ marginBottom: 16, paddingTop: 16 }}>
        <div style={{ flex: 1 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>PO ID</Text>
          <Flex gap={8}>
            <InputNumber
              value={poId}
              onChange={(v) => setPoId(v)}
              placeholder="Enter PO ID"
              min={1}
              style={{ flex: 1 }}
            />
            <Button onClick={() => poId && loadPO(poId)} loading={poLoading} disabled={!poId}>
              Load PO
            </Button>
          </Flex>
        </div>
        <div style={{ flex: 1 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Delivery Note Ref</Text>
          <Input
            value={deliveryNoteRef}
            onChange={(e) => setDeliveryNoteRef(e.target.value)}
            placeholder="DN reference"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Supplier Invoice</Text>
          <Input
            value={supplierInvoice}
            onChange={(e) => setSupplierInvoice(e.target.value)}
            placeholder="Invoice reference"
          />
        </div>
      </Flex>

      {poDetail && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          PO {poDetail.poCode} — Vendor {poDetail.vendorId} — {poDetail.lineCount} lines
        </Text>
      )}

      {lines.length > 0 && (
        <>
          <Divider>Receipt Lines</Divider>
          <Table
            dataSource={lines}
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ x: 900 }}
            columns={[
              { title: 'Item', dataIndex: 'itemDescription', key: 'itemDescription', width: 180 },
              { title: 'PO Qty', dataIndex: 'qty', key: 'qty', width: 70, align: 'right' as const },
              { title: 'Balance', dataIndex: 'balanceQty', key: 'balanceQty', width: 80, align: 'right' as const },
              {
                title: 'Delivered', key: 'deliveredQty', width: 100,
                render: (_, record: GrnLineInput) => (
                  <InputNumber
                    value={record.deliveredQty}
                    onChange={(v) => updateLine(record.key, 'deliveredQty', v ?? 0)}
                    min={0.01}
                    max={record.balanceQty * 1.5}
                    size="small"
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Accepted', key: 'acceptedQty', width: 100,
                render: (_, record: GrnLineInput) => (
                  <InputNumber
                    value={record.acceptedQty}
                    onChange={(v) => updateLine(record.key, 'acceptedQty', v ?? 0)}
                    min={0}
                    max={record.deliveredQty}
                    size="small"
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Rejected', key: 'rejectedQty', width: 100,
                render: (_, record: GrnLineInput) => (
                  <InputNumber
                    value={record.rejectedQty}
                    onChange={(v) => updateLine(record.key, 'rejectedQty', v ?? 0)}
                    min={0}
                    max={record.deliveredQty}
                    size="small"
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Inspection', key: 'inspectionStatus', width: 120,
                render: (_, record: GrnLineInput) => (
                  <Select
                    value={record.inspectionStatus}
                    onChange={(v) => updateLine(record.key, 'inspectionStatus', v)}
                    size="small"
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Passed', value: 'passed' },
                      { label: 'Failed', value: 'failed' },
                      { label: 'Partial', value: 'partial' },
                    ]}
                  />
                ),
              },
            ]}
          />
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>Notes</Text>
        <Input.TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Optional notes..."
        />
      </div>
    </Modal>
  );
}

// ── Main Page ──

export function GRNListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<GRNDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleFetch = useCallback(async (params: FetchParams) => {
    return fetchGRNs(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const openDetail = async (id: number) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    try {
      const data = await getGRN(id);
      setDetail(data);
    } catch {
      message.error('Failed to load GRN details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Goods Receipt Notes</Title>
      </Flex>

      <DataTable<GRNHeader>
        columns={columns}
        fetchData={handleFetch}
        rowKey="id"
        toolbar={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New GRN
          </Button>
        }
        onRow={(record) => ({
          onClick: () => openDetail(record.id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 900 }}
      />

      <CreateGRNModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <DetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
        title={detail?.grnCode || ''}
        subtitle={detail ? `PO: ${detail.poHeader?.poCode || detail.poHeaderId}` : undefined}
        loading={detailLoading}
        width={800}
        tabs={detail ? [
          {
            key: 'details',
            label: 'Details',
            icon: <FileTextOutlined />,
            children: (
              <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="GRN Code">{detail.grnCode}</Descriptions.Item>
                <Descriptions.Item label="PO">{detail.poHeader?.poCode || detail.poHeaderId}</Descriptions.Item>
                <Descriptions.Item label="Delivery Note">{detail.deliveryNoteRef || '—'}</Descriptions.Item>
                <Descriptions.Item label="Supplier Invoice">{detail.supplierInvoice || '—'}</Descriptions.Item>
                <Descriptions.Item label="Received On">
                  {detail.receivedOn ? dayjs(detail.receivedOn).format(DISPLAY_DATE_FORMAT) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Lines">{detail.lines?.length ?? 0}</Descriptions.Item>
                {detail.notes && (
                  <Descriptions.Item label="Notes" span={2}>{detail.notes}</Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
          {
            key: 'lines',
            label: `Lines (${detail.lines?.length ?? 0})`,
            icon: <UnorderedListOutlined />,
            children: (
              <Table
                dataSource={detail.lines}
                columns={grnLineColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              />
            ),
          },
        ] : undefined}
      />
    </div>
  );
}
